import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../hooks/UserContext";
import { fetchGetProductosAdministracion } from "../services/productoService";
import { isAbortError } from "../services/fetchService";
import LocalSelector from "./components/LocalSelector";
import DataGridViewport from "../components/DataGridViewport";

const AdministracionProductos = () => {
  const navigate = useNavigate();
  const { getUser } = useUserContext();
  const user = useMemo(() => getUser(), [getUser]);
  const userToken = user?.token;
  const [idLocal, setIdLocal] = useState<string | undefined>(undefined);
  const [productos, setProductos] = useState<any[]>([]);
  const [totalProductos, setTotalProductos] = useState(0);
  const [activePriceFields, setActivePriceFields] = useState<string[]>([]);
  const [headers, setHeaders] = useState<Record<string, string>>({});
  const [filterText, setFilterText] = useState("");
  const [searchToApply, setSearchToApply] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const canUpdatePrices = user?.permissions?.includes("actualizar_precios");

  useEffect(() => {
    if (!userToken || !user || !idLocal) {
      setProductos([]);
      setTotalProductos(0);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchGetProductosAdministracion(idLocal, user, paginationModel.page, paginationModel.pageSize, searchToApply, controller.signal)
      .then((data) => {
        setProductos(data.items);
        setTotalProductos(data.total);
        setActivePriceFields(data.activePriceFields);
        setHeaders(data.headers);
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        console.log(err);
        setProductos([]);
        setTotalProductos(0);
        setError("No se pudieron cargar los productos.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [idLocal, paginationModel.page, paginationModel.pageSize, searchToApply, user, userToken]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      setSearchToApply(filterText);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [filterText]);

  const priceFields = useMemo(() => {
    const normalize = (fields: string[]) => Array.from(new Set(fields.map((field) => field?.trim()).filter(Boolean)));
    if (activePriceFields.length > 0) {
      return normalize(activePriceFields);
    }

      return normalize(["CONTADO", "MAYOR", "MENOR"]);
  }, [activePriceFields]);

  const columns = useMemo<GridColDef[]>(() => {
    const productFields = productos[0] ? Object.keys(productos[0]) : [];
    const hiddenFields = new Set(["IDPRODUCTO"]);
    const isHiddenField = (field: string) => {
      const normalized = field.trim().toUpperCase();
      return hiddenFields.has(field) || normalized.endsWith(" B") || normalized.endsWith("_B");
    };
    const shownPriceFields = priceFields.filter((field) => productFields.includes(field));
    const preferredFields = [
      "CODIGO",
      "PRODUCTO",
      "IVAVENTA",
      "PRECIOCOMPRA",
      ...shownPriceFields,
      "DESCRIPCION",
      "STOCK",
      "PRESENTACION",
      "UxB",
      "CREADOR",
    ].filter((field, index, fields) =>
      productFields.includes(field)
      && !isHiddenField(field)
      && fields.indexOf(field) === index
    );
    const dynamicFields = productFields.filter((field) =>
      !isHiddenField(field)
      && !preferredFields.includes(field)
    );

    const buildColumn = (field: string): GridColDef => {
      const isNumeric = shownPriceFields.includes(field) || ["IVAVENTA", "PRECIOCOMPRA", "STOCK", "UxB"].includes(field);
      return {
        field,
        headerName: headers[field] ?? field,
        minWidth: field === "PRODUCTO" ? 260 : 130,
        flex: field === "PRODUCTO" ? 1.5 : 0.7,
        align: isNumeric ? "right" : "left",
        headerAlign: isNumeric ? "right" : "left",
      };
    };

    const baseColumns: GridColDef[] = [
      ...preferredFields.map(buildColumn),
      ...dynamicFields.map(buildColumn),
    ];

    if (canUpdatePrices) {
      baseColumns.push({
        field: "EDITAR",
        headerName: "Acciones",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<Inventory2Icon />}
            onClick={() => navigate(`/updateproducto/${params.row.IDPRODUCTO}`)}
          >
            Editar
          </Button>
        ),
      });
    }

    return baseColumns;
  }, [canUpdatePrices, headers, navigate, priceFields, productos]);

  const rows = useMemo(
    () => productos.map((producto, index) => ({
      id: `${producto.IDPRODUCTO}-${producto.CODIGO}-${index}`,
      ...producto,
    })),
    [productos]
  );

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>
        Administracion de productos
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <LocalSelector onLocalChange={setIdLocal} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label="Buscar"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<PriceChangeIcon />}
              onClick={() => navigate("/listaprecios")}
              fullWidth
            >
              Lista de precios
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length > 0 || loading ? (
        <DataGridViewport height={650} minWidth={1120}>
          <DataGrid
            rows={rows}
            columns={columns}
            rowCount={totalProductos}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            loading={loading}
            disableRowSelectionOnClick
          />
        </DataGridViewport>
      ) : idLocal ? (
        <Alert severity="info">No se encontraron productos.</Alert>
      ) : (
        <Alert severity="info">Seleccione un local para administrar productos.</Alert>
      )}
    </Box>
  );
};

export default AdministracionProductos;
